import { __decorate } from "tslib";
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import SourceRepository from '@/services/repository/SourceRepository';
import { BoundaryDTO, Boundary } from '@/entities/models/Boundary';
import BoundaryInfo from '@/views/components/boundary/BoundaryInfo.vue';
import BoundaryInfoWithEdit from '@/views/components/boundary/BoundaryInfoWithEdit.vue';
import BoundaryRepository from '@/services/repository/BoundaryRepository';
import VueUtilities from '@/services/VueUtilities';
import CalibrationStatus from '@/entities/enums/CalibrationStatus';
import CompanyLoggerRepository from '@/services/repository/CompanyLoggerRepository';
import CreateRuleWarning from '@/views/components/notification/CreateRuleWarning.vue';
import DecimalConversion from '@/services/DecimalConversion';
import UserRepository from '@/services/repository/UserRepository';
import AppConfig from '@/configLoader';
import { ServiceState } from '@/entities/enums/ServiceState';
import CheckboxSelect from '../generals/CheckboxSelect.vue';
import { latLng, Control } from 'leaflet';
import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet';
import { EXP_OPTIONS } from '@/entities/enums/LoggersCalibrationExpirationOptions';
var sourceRepository;
var boundaryRepository;
var companyLoggerRepository;
var userRepository;
const MONTHS_IN_YEAR = 12;
let LoggerInfo = class LoggerInfo extends Vue {
    constructor() {
        super(...arguments);
        this.demo = AppConfig.getConfig().demo.name;
        this.selectedQuantities = [];
        this.calibrationExpOptions = EXP_OPTIONS;
        this.CalibrationStatus = CalibrationStatus;
        this.ServiceState = ServiceState;
        this.nameExists = false;
        this.sourceName = null;
        this.sourceDescription = null;
        this.selectedGroup = null;
        this.mapObject = null;
        this.zoomControlInstance = null;
        this.zoom = 13;
        this.center = latLng(48.61451, 19.14149);
        this.url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        this.attribution = '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors';
        this.altitude = null;
        this.longitude = null;
        this.latitude = null;
        this.boundaries = null;
        this.originBoundaries = null;
        this.isLoading = false;
        this.createRuleWarningVisible = false;
        this.unasignedSourcesIds = [];
        this.selectedVisibility = null;
        this.isEditing = false;
        this.currentUserName = null;
        this.isCustomExpirationEditing = false;
        this.customExpiration = null;
        this.backupCustomExpiration = null;
    }
    onIsEditingChange() {
        this.updateMap();
    }
    onMarkerCoordinatesChange(newCoordinates) {
        if (newCoordinates) {
            this.centerMap(newCoordinates);
        }
    }
    get hasCalibrationProtocolToDownload() {
        return !(this.source.logger == null ||
            this.source.logger.calibration == null ||
            this.source.logger.calibration.calibratedAt <= 0 ||
            this.source.logger.calibration.notAvailable);
    }
    /*
     * check if this feature is allowed in current department
     */
    get customExpirationIsAllowed() {
        return this.tier?.features.find((x) => x.Name === 'Loggers.Calibration.Custom')?.Value?.Allowed === true;
    }
    get selectedGroupName() {
        return !this.selectedGroup || !this.selectedGroup.name ? this.$t('generals.unassigned') : this.selectedGroup.name;
    }
    get nameChanged() {
        return this.sourceName && this.sourceName != this.source.name;
    }
    get descriptionChanged() {
        return this.sourceDescription != this.source.description;
    }
    get visibilityChanged() {
        return this.selectedVisibility != this.source.visible;
    }
    get hasCustomExpirationChanged() {
        return this.backupCustomExpiration != this.customExpiration;
    }
    get vqsChanged() {
        return !(this.selectedQuantities.every((x) => this.source.enabledVirtualQuantities.includes(x)) &&
            this.source.enabledVirtualQuantities.every((x) => this.selectedQuantities.includes(x)));
    }
    get sourceGroupChanged() {
        if (!this.source.sourceGroup && this.selectedGroup.id == null)
            return false;
        else if (!this.source.sourceGroup && this.selectedGroup.id != null)
            return true;
        else {
            return !(this.source.sourceGroup.id == this.selectedGroup.id);
        }
    }
    get hasBoundariesChanged() {
        return !BoundaryDTO.compareBoundaries(this.originBoundaries, this.boundaries);
    }
    get hasCoordinatesChanged() {
        return (this.altitude != this.source.coordinates?.Altitude ||
            this.latitude != this.source.coordinates?.Latitude ||
            this.longitude != this.source.coordinates?.Longitude);
    }
    //This is called from parent component
    get hasChanges() {
        return (this.nameChanged ||
            this.descriptionChanged ||
            this.sourceGroupChanged ||
            this.hasBoundariesChanged ||
            this.visibilityChanged ||
            this.vqsChanged ||
            this.hasCoordinatesChanged ||
            this.hasCustomExpirationChanged);
    }
    get canHaveBoundaries() {
        return this.selectedGroup == null || this.selectedGroup?.id == null;
    }
    get noCoordinates() {
        let hasAtmPressure = this.source.logger?.allowedTypes.some((x) => x.slug === 'atmosphericpressure');
        let hasCoordinates = this.source.coordinates?.Altitude == null ||
            this.source.coordinates?.Latitude == null ||
            this.source.coordinates?.Longitude == null;
        return hasAtmPressure && hasCoordinates;
    }
    get mapOptions() {
        return {
            zoomControl: false,
            scrollWheelZoom: false,
            touchZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            dragging: false,
            closePopupOnClick: false,
            zoomSnap: 0.5
        };
    }
    get markerCoordinates() {
        if (this.longitude !== null && this.latitude !== null)
            return latLng(this.latitude, this.longitude);
        return null;
    }
    centerMap(coordinates) {
        if (this.mapObject) {
            this.mapObject.setView(coordinates, this.zoom);
        }
    }
    onMarkerReady(marker) {
        marker.openPopup();
    }
    onMapClick(event) {
        this.longitude = event.latlng.lng;
        this.latitude = event.latlng.lat;
        this.centerMap(latLng(event.latlng.lat, event.latlng.lng));
    }
    onZoomChange(newZoomLevel) {
        this.zoom = newZoomLevel;
    }
    updateMap() {
        if (!this.mapObject || !this.zoomControlInstance)
            return;
        if (this.isEditing) {
            this.mapObject.scrollWheelZoom.enable();
            this.mapObject.dragging.enable();
            this.mapObject.touchZoom.enable();
            this.mapObject.doubleClickZoom.enable();
            this.mapObject.boxZoom.enable();
            this.mapObject.addControl(this.zoomControlInstance);
        }
        else {
            this.mapObject.scrollWheelZoom.disable();
            this.mapObject.dragging.disable();
            this.mapObject.touchZoom.disable();
            this.mapObject.doubleClickZoom.disable();
            this.mapObject.boxZoom.disable();
            this.mapObject.removeControl(this.zoomControlInstance);
            this.onZoomChange(13);
        }
        this.centerMap(this.markerCoordinates);
    }
    getFormattedDate(timestamp) {
        return this.dateTimeManager.formatTime(new Date(timestamp * 1000), 'UTC', null, '');
    }
    quantityArrChanged(quantityArr) {
        this.selectedQuantities = quantityArr;
    }
    async mounted() {
        this.$nextTick(() => {
            this.mapObject = this.$refs.coordsMap?.mapObject;
            this.zoomControlInstance = new Control.Zoom({ position: 'topleft' });
            if (this.markerCoordinates) {
                this.centerMap(this.markerCoordinates);
            }
        });
    }
    async created() {
        sourceRepository = new SourceRepository(this);
        boundaryRepository = new BoundaryRepository(this);
        companyLoggerRepository = new CompanyLoggerRepository(this);
        userRepository = new UserRepository(this);
        this.loadUser();
        this.assignDefaultValues();
    }
    async loadUser() {
        let currentUser = await userRepository.getCurrentUser();
        this.currentUserName = currentUser.username;
    }
    get isDemo() {
        return this.currentUserName == this.demo;
    }
    assignDefaultValues() {
        this.sourceName = this.source.name;
        this.sourceDescription = this.source.description;
        this.selectedVisibility = this.source.visible;
        this.boundaries = Boundary.convertBoundariesToDTO(this.source.boundaries);
        this.originBoundaries = Boundary.convertBoundariesToDTO(this.source.boundaries);
        this.selectedQuantities = this.source.enabledVirtualQuantities;
        this.longitude = this.source.coordinates?.Longitude || null;
        this.latitude = this.source.coordinates?.Latitude || null;
        this.altitude = this.source.coordinates?.Altitude || null;
        if (this.source.logger != null && this.source.logger.calibration != null)
            this.assignCustomExpiration();
        this.assignSourceGroupToForm();
    }
    assignCustomExpiration() {
        if (this.source.logger.calibration.expiresAt) {
            // convert diff from secs into years
            const diffInYears = Math.round((this.source.logger.calibration.expiresAt - this.source.logger.calibration.calibratedAt) / (60 * 60 * 24 * 365));
            const currentExpInYears = Number(this.calibrationExpOptions.find((y) => y == diffInYears));
            if (currentExpInYears) {
                this.backupCustomExpiration = currentExpInYears;
                this.customExpiration = currentExpInYears;
            }
            else {
                this.clearCustomExpiration();
            }
        }
    }
    clearCustomExpiration() {
        this.customExpiration = null;
    }
    async downloadCalibration() {
        this.isLoading = true;
        await companyLoggerRepository.downloadCalibrationProtocol(this.source.logger.companyLoggerId);
        this.isLoading = false;
    }
    getCopyOfBoundaries() {
        if (!this.boundaries)
            return [];
        else
            return JSON.parse(JSON.stringify(this.boundaries));
    }
    //This is called from parent component
    validate() {
        if (this.$refs.boundariesComponent)
            this.$refs.boundariesComponent.validate();
        else {
            this.onBoundaryValidation(true);
        }
    }
    onBoundaryValidation(result) {
        if (result == true) {
            this.saveChanges();
        }
    }
    async saveChanges() {
        await this.checkIfNameExists(this.sourceName);
        if (this.nameExists) {
            VueUtilities.openErrorToast(this, this.$t('component.location.detail.msg.unique_name').toString());
        }
        this.$validator.validateAll().then(async (result) => {
            if (result && !this.nameExists) {
                let allPromises = [];
                if (this.hasChanges) {
                    let sourceDTO = {
                        Name: this.sourceName,
                        Description: this.sourceDescription,
                        Visible: this.selectedVisibility,
                        Coordinates: {
                            Latitude: this.latitude,
                            Longitude: this.longitude,
                            Altitude: this.altitude
                        }
                    };
                    allPromises.push(sourceRepository.updateSource(this.source.id, sourceDTO));
                }
                let showWarning = false;
                if (this.sourceGroupChanged) {
                    if (this.selectedGroup.id == null) {
                        allPromises.push(sourceRepository.unassignFromGroup(this.source.id, this.source.sourceGroup.id));
                        showWarning = true;
                    }
                    else {
                        allPromises.push(sourceRepository.assignToGroup(this.source.id, this.selectedGroup.id));
                    }
                }
                if (this.canHaveBoundaries && this.hasBoundariesChanged) {
                    this.boundaries.forEach((boundary) => {
                        boundary.lowerAlarmBoundary = DecimalConversion.toFloat(boundary.lowerAlarmBoundary);
                        boundary.upperAlarmBoundary = DecimalConversion.toFloat(boundary.upperAlarmBoundary);
                        boundary.lowerWarningBoundary = DecimalConversion.toFloat(boundary.lowerWarningBoundary);
                        boundary.upperWarningBoundary = DecimalConversion.toFloat(boundary.upperWarningBoundary);
                    });
                    allPromises.push(this.assignBoundaries(this.boundaries, this.source.id));
                }
                if (this.vqsChanged) {
                    let added = this.selectedQuantities.filter((x) => !this.source.enabledVirtualQuantities.includes(x));
                    let deleted = this.source.enabledVirtualQuantities.filter((x) => !this.selectedQuantities.includes(x));
                    if (added && added.length > 0) {
                        added.forEach((x) => {
                            allPromises.push(sourceRepository.enableQuantities(this.source.id, x.key));
                        });
                    }
                    if (deleted && deleted.length > 0) {
                        deleted.forEach((x) => {
                            allPromises.push(sourceRepository.disableQuantities(this.source.id, x.key));
                        });
                    }
                }
                if (this.hasCustomExpirationChanged) {
                    allPromises.push(companyLoggerRepository.saveLoggerCalibrationExpiration(this.customExpiration * MONTHS_IN_YEAR, this.source.logger.companyLoggerId));
                }
                Promise.all(allPromises).then((x) => {
                    VueUtilities.openSuccessToast(this, this.$t('component.location.detail.msg.edit_success').toString());
                    this.$validator.pause();
                    this.assignDefaultValues();
                    this.isEditing = false;
                    if (showWarning) {
                        this.unasignedSourcesIds = [this.source.id];
                        this.createRuleWarningVisible = true;
                    }
                    else {
                        this.$emit('reloadSource');
                        this.$emit('editingLogger');
                    }
                });
            }
        });
    }
    warningClosed() {
        this.$emit('reloadSource');
        this.$emit('editingLogger');
    }
    async assignBoundaries(boundaries, sourceId) {
        return boundaryRepository.createBoundary(boundaries, sourceId);
    }
    async checkIfNameExists(name) {
        if (!name || name == this.source.name) {
            this.nameExists = false;
        }
        else {
            let checkResponse = await sourceRepository.checkIfNameExists(name);
            this.nameExists = checkResponse.Exist;
        }
    }
    groupSelected(group) {
        this.selectedGroup = group;
    }
    boundariesChanged(boundaries) {
        this.boundaries = boundaries;
    }
    assignSourceGroupToForm() {
        if (!this.source.sourceGroup)
            this.selectedGroup = this.sourceGroups.find((x) => !x.id);
        else
            this.selectedGroup = this.sourceGroups.find((x) => x.id == this.source.sourceGroup.id);
    }
    editLogger() {
        this.isEditing = !this.isEditing;
        if (!this.isEditing)
            this.assignDefaultValues();
        this.$emit('editingLogger');
    }
    async changePausedMeasurementFlag() {
        let sourceDTO = {
            Name: this.source.name,
            Description: this.source.description,
            Visible: this.source.visible,
            PausedMeasurement: !this.source.pausedMeasurement,
            Coordinates: this.source.coordinates
        };
        let updateResult = await sourceRepository.updateSource(this.source.id, sourceDTO);
        if (updateResult) {
            VueUtilities.openSuccessToast(this, this.$t('component.location.detail.msg.edit_success').toString());
            this.$emit('reloadSource');
        }
    }
    editService() {
        this.$emit('editService');
    }
};
__decorate([
    Prop({ type: Object })
], LoggerInfo.prototype, "source", void 0);
__decorate([
    Prop({ type: Array })
], LoggerInfo.prototype, "sourceGroups", void 0);
__decorate([
    Prop({ type: Array, default: [] })
], LoggerInfo.prototype, "quantities", void 0);
__decorate([
    Prop({ type: Array })
], LoggerInfo.prototype, "eventTypes", void 0);
__decorate([
    Prop({ type: Object })
], LoggerInfo.prototype, "dateTimeManager", void 0);
__decorate([
    Prop({ type: Object })
], LoggerInfo.prototype, "tier", void 0);
__decorate([
    Watch('isEditing')
], LoggerInfo.prototype, "onIsEditingChange", null);
__decorate([
    Watch('markerCoordinates')
], LoggerInfo.prototype, "onMarkerCoordinatesChange", null);
LoggerInfo = __decorate([
    Component({
        components: {
            BoundaryInfo,
            CreateRuleWarning,
            BoundaryInfoWithEdit,
            CheckboxSelect,
            LMap,
            LTileLayer,
            LMarker,
            LPopup
        }
    })
], LoggerInfo);
export default LoggerInfo;
